/**
 * AppShell — signed-in chrome, after the enforza-neon console:
 *   • top bar: brand glyph (home), breadcrumb with a lime anchor bar, stage chip,
 *     theme toggle, account menu, and a burger on mobile
 *   • side nav: grouped sections, collapsible to an icon rail (remembered),
 *     sign-out and the Powered-by credit at the bottom; a slide-in drawer on mobile
 *   • main: the routed page, scrolling independently of the chrome
 * Styling lives in shell.css.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { ChevronRight, ChevronsLeft, ChevronsRight, Copy, LogOut, Menu } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { config } from "../config";
import { BreadcrumbProvider, useBreadcrumbLabels } from "../breadcrumbs";
import { navItems, navSections, widgetTabs, type NavItem } from "../sections";
import { BrandMark, PoweredBy } from "./BrandMark";
import { ThemeToggle } from "./ThemeToggle";
import "../shell.css";

const COLLAPSED_KEY = "xq-sidenav-collapsed";

export function AppShell() {
  // Desktop: expanded or icon-rail, remembered across reloads
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === "1";
    } catch {
      return false;
    }
  });
  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      try {
        localStorage.setItem(COLLAPSED_KEY, prev ? "0" : "1");
      } catch {
        // preference just won't persist
      }
      return !prev;
    });
  }, []);

  // Mobile: the side nav is a drawer, closed again on every navigation
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setMobileOpen(false), [location.pathname]);

  return (
    <BreadcrumbProvider>
    <div className="app">
      <TopBar collapsed={collapsed} onMobileMenu={() => setMobileOpen((o) => !o)} />
      <div className="app-body">
        <SideNav collapsed={collapsed} onToggle={toggleCollapsed} mobileOpen={mobileOpen} />
        {mobileOpen && <div className="mobile-backdrop" onClick={() => setMobileOpen(false)} aria-hidden="true" />}
        <main className="app-main">
          <Outlet />
        </main>
      </div>
    </div>
    </BreadcrumbProvider>
  );
}

// ─── Top bar ──────────────────────────────────────────────────────────────────

// `collapsed` mirrors the side nav: when it is an icon rail, the brand shrinks to
// the glyph and the breadcrumb moves left to stay aligned with the page content.
function TopBar({ collapsed, onMobileMenu }: { collapsed: boolean; onMobileMenu: () => void }) {
  return (
    <header className="topbar">
      <div className="topbar-row">
        <Link to="/dashboard" aria-label="Dashboard" className={`topbar-brand${collapsed ? " is-rail" : ""}`}>
          <BrandMark size={30} nameSize={26} />
        </Link>
        <div className="topbar-crumbs">
          <Breadcrumbs />
        </div>
        <div className="topbar-spacer" />
        <div className="topbar-actions">
          {/* Make non-prod environments obvious at a glance */}
          {config.stage !== "prod" && <span className="stage-chip">{config.stage}</span>}
          <AccountMenu />
          <button type="button" className="icon-chip mobile-burger" onClick={onMobileMenu} aria-label="Open navigation" title="Open navigation">
            <Menu size={18} />
          </button>
        </div>
      </div>
      {/* Phones: breadcrumb gets its own row under the actions */}
      <div className="topbar-crumb-row">
        <Breadcrumbs />
      </div>
    </header>
  );
}

// Path segment → label, from the nav plus anything not in it
// (keyed by the last path segment, so "tools/new" labels the "new" crumb)
const CRUMB_LABELS: Record<string, string> = Object.fromEntries([...navItems, ...widgetTabs].map((i) => [i.path.split("/").pop()!, i.label]));
// Whole nav paths first, so "tools/new" and "quotes/new" each keep their own label
const PATH_LABELS: Record<string, string> = Object.fromEntries(navItems.map((i) => [i.path, i.label]));

function Breadcrumbs() {
  const { pathname } = useLocation();
  const registered = useBreadcrumbLabels();
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return null;
  return (
    <nav aria-label="Breadcrumb" className="breadcrumbs">
      {segments.map((seg, i) => {
        const href = "/" + segments.slice(0, i + 1).join("/");
        // Unknown segments are usually IDs: show a short prefix
        // Page-registered name (e.g. widget name), then static label, then short ID
        const label = PATH_LABELS[href.slice(1)] ?? registered[seg] ?? CRUMB_LABELS[seg] ?? (seg.length > 12 ? `${seg.slice(0, 8)}…` : seg);
        const current = i === segments.length - 1;
        return (
          <span key={href} className="crumb">
            {i > 0 && <ChevronRight size={12} strokeWidth={2.5} aria-hidden="true" className="crumb-sep" />}
            {current ? (
              <span aria-current="page" className="crumb-current">
                {label}
              </span>
            ) : (
              <Link to={href} className="crumb-link">
                {label}
              </Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}

// Initials chip that opens a small menu with the user's email, company ID and sign-out
function AccountMenu() {
  const { user, signOutEverywhere } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close when clicking anywhere outside the menu, or pressing Escape
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function copyCompanyId() {
    if (!user?.companyId) return;
    navigator.clipboard?.writeText(user.companyId).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  }

  const initial = ((user?.givenName || user?.email)?.[0] ?? "?").toUpperCase();
  // SSO avatar (e.g. the Google profile photo); falls back to the initial if it can't load
  const [photoFailed, setPhotoFailed] = useState(false);
  const photo = user?.picture && !photoFailed ? user.picture : null;
  return (
    <div className="account" ref={ref}>
      <button type="button" className="icon-chip avatar" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open} aria-label="Account" title={user?.name ? `${user.name} (${user.email})` : (user?.email ?? "Account")}>
        {photo ? <img className="avatar-photo" src={photo} alt="" referrerPolicy="no-referrer" onError={() => setPhotoFailed(true)} /> : initial}
      </button>
      {open && (
        <div className="account-menu" role="menu">
          <div className="account-menu-label">Signed in as</div>
          <div className="account-menu-email">{user?.email}</div>
          <div className="account-menu-label">Company ID</div>
          <button type="button" className="account-menu-id" onClick={copyCompanyId} title="Copy company ID">
            <code>{user?.companyId ?? "not assigned yet"}</code>
            {user?.companyId && <Copy size={12} aria-hidden="true" />}
          </button>
          {copied && <div className="account-menu-copied">Copied</div>}
          <div className="account-menu-sep" />
          {/* Light/dark lives here, out of the way: the console is light unless chosen */}
          <ThemeToggle />
          <button
            type="button"
            role="menuitem"
            className="account-menu-item"
            onClick={() => {
              signOutEverywhere();
              navigate("/login", { replace: true });
            }}
          >
            <LogOut size={14} aria-hidden="true" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Side nav ─────────────────────────────────────────────────────────────────

function SideNav({ collapsed, onToggle, mobileOpen }: { collapsed: boolean; onToggle: () => void; mobileOpen: boolean }) {
  const { signOutEverywhere } = useAuth();
  const navigate = useNavigate();
  // The mobile drawer always shows labels
  const rail = mobileOpen ? false : collapsed;

  return (
    <aside className={`sidenav${rail ? " is-rail" : ""}${mobileOpen ? " is-mobile-open" : ""}`}>
      <div className="sidenav-sections">
        {navSections.map((section, i) => (
          <div key={section.label} className="sidenav-section">
            {rail ? (
              // Rail mode: the collapse toggle sits alone above the icons
              i === 0 ? (
                <div className="sidenav-rail-toggle">
                  <CollapseToggle collapsed={rail} onToggle={onToggle} />
                </div>
              ) : (
                <div className="sidenav-rail-gap" />
              )
            ) : (
              <div className="sidenav-section-head">
                <span>{section.label}</span>
                {i === 0 && <CollapseToggle collapsed={rail} onToggle={onToggle} />}
              </div>
            )}
            <div className="sidenav-items">
              {section.items.map((item) => (
                <NavRow key={item.path} item={item} rail={rail} />
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="sidenav-foot">
        <button
          type="button"
          className="nav-row"
          title="Sign out"
          onClick={() => {
            signOutEverywhere();
            navigate("/login", { replace: true });
          }}
        >
          <LogOut size={16} aria-hidden="true" />
          {!rail && <span>Sign out</span>}
        </button>
        {!rail && (
          <div className="sidenav-powered">
            <PoweredBy />
          </div>
        )}
      </div>
    </aside>
  );
}

function CollapseToggle({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const label = collapsed ? "Expand sidebar" : "Collapse sidebar";
  return (
    <button type="button" className="collapse-toggle" onClick={onToggle} title={label} aria-label={label}>
      {collapsed ? <ChevronsRight size={14} /> : <ChevronsLeft size={14} />}
    </button>
  );
}

function NavRow({ item, rail }: { item: NavItem; rail: boolean }) {
  const Icon = item.icon;
  const { pathname } = useLocation();
  // Not built yet: greyed out, not clickable
  if (item.soon) {
    return (
      <div className="nav-row is-soon" title={`${item.label}: coming soon`}>
        <Icon size={16} aria-hidden="true" />
        {!rail && (
          <>
            <span className="nav-row-label">{item.label}</span>
            <span className="soon-chip">Soon</span>
          </>
        )}
      </div>
    );
  }
  return (
    <NavLink
      to={`/${item.path}`}
      end
      // NavLink adds "active" for its own path; activeFor widens that (e.g. Your tools inside a tool)
      className={({ isActive }) => `nav-row${isActive || item.activeFor?.(pathname) ? " active" : ""}`}
      title={rail ? item.label : undefined}
    >
      <span className="nav-row-bar" aria-hidden="true" />
      <Icon size={16} aria-hidden="true" />
      {!rail && <span className="nav-row-label">{item.label}</span>}
    </NavLink>
  );
}
